import { useEffect, useRef } from 'react'
import * as Analytics from 'summer-analytics-client'
import { APP_NAME, VERSION } from '@/constants'
import { useSession } from 'next-auth/react'
import { useRouter } from 'next/router'

const useEventTracking = (email: string | undefined) => {
  const firstRun = useRef(true)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return
    }
    if (firstRun.current) {
      Analytics.init({
        user: email,
        app_name: APP_NAME,
        app_version: VERSION,
      })
      firstRun.current = false
    } else {
      Analytics.setUserId(email)
    }
  }, [email])
}

// Track page views
const usePageViewTracking = (userId: string | undefined, email: string | undefined) => {
  const router = useRouter()
  const lastPath = useRef<string | null>(null)
  
  useEffect(() => {
    if (!email || !userId || process.env.NODE_ENV !== 'production') {
      return
    }
    
    const handleRouteChange = (url: string) => {
      // Only track if the path is different from the last tracked path
      if (url !== lastPath.current) {
        lastPath.current = url
        
        // Track the page view in Analytics
        Analytics.trackEvent('page_view', {
          path: url,
          title: document.title,
        })
        
        // Track in backend (if user is logged in)
        if (email && userId) {
          fetch('/api/v1/users/track-page-view', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              path: url,
              title: document.title,
            }),
          }).catch((error) => {
            console.error('Failed to track page view:', error)
          })
        }
      }
    }
    
    // Track initial page load
    handleRouteChange(router.asPath)
    
    // Track subsequent route changes
    router.events.on('routeChangeComplete', handleRouteChange)
    
    // Cleanup
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router, email, userId])
}

export const Tracker = () => {
  const isClient = typeof window !== 'undefined'
  if (!isClient) {
    return null
  }
  const session = useSession()
  const email = session?.data?.user?.email ?? undefined
  const userId = session?.data?.user?.id ?? undefined
  
  useEventTracking(email)
  usePageViewTracking(userId, email)
  
  return null
}
